import { useEffect, useRef } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { useDashboardContext } from "../../../context-state/DashboardContext";
import { useEventDashboardData } from "../../../hooks/useEventDashboardData";
import { useSnackbar } from "../../../components/minimal/snackbar";
import { useLocales } from "../../../locales";
import { LinearLoader } from "../../../components/linear-loader/LinearLoader";

export const EventGuard = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { translate } = useLocales();
    const { eventContext, eventOrderContext, ticketContext } = useDashboardContext();
    const { isLoading, fetchEventDashboardData } = useEventDashboardData();

    const { getEvent } = eventContext;
    const { getEventOrdersOfEvent } = eventOrderContext;
    const { getTicketsOfEvent } = ticketContext;

    const { eventId } = useParams();
    const location = useLocation();

    const event = getEvent(eventId);
    const eventTickets = getTicketsOfEvent(eventId);
    const eventOrders = getEventOrdersOfEvent(eventId);

    // Tabs that never read the orders / userEvents lists. On a big event those two
    // responses are several MB each, so these tabs load a light data-set instead.
    const TABS_WITHOUT_ORDERS = /\/(sell-stats|links)(\/)?$/;
    const tabNeedsOrders = !TABS_WITHOUT_ORDERS.test(location.pathname);

    // Unchanged from before this fix: these tabs all read the order list, so they refetch
    // on entry rather than risk acting on a stale view.
    const TABS_REFETCHED_ON_ENTRY = /\/(coupons|seats-chart)(\/)?$/;

    const lastFetchedPath = useRef(null);

    // The effect re-runs whenever the relation lengths change, and a fetch takes seconds on
    // a large event — so a response landing mid-flight used to re-trigger the very fetch
    // that was still running, issuing the whole data-set twice. Keyed by shape so a light
    // fetch in flight doesn't block the full one the orders tab needs, and held in a set so
    // a light fetch finishing cannot clear the key of a full fetch started after it.
    const inFlightFetches = useRef(new Set());

    const getAllUserEventRelevantData = async ({ includeOrders = true, includeExtras = true, silent = false } = {}) => {
        const fetchKey = `${event._id}:${includeOrders ? 'full' : 'light'}:${includeExtras ? 'extras' : 'lists'}`;
        if (inFlightFetches.current.has(fetchKey)) return true;
        inFlightFetches.current.add(fetchKey);
        try {
            const ok = await fetchEventDashboardData(event, { includeOrders, includeExtras, silent });
            if (!ok && !silent) {
                enqueueSnackbar(translate("general_error"), { variant: "error" });
            }
            return ok;
        } finally {
            inFlightFetches.current.delete(fetchKey);
        }
    };

    useEffect(() => {
        if (!event) return;

        const ticketsMissing = !eventTickets;
        const ordersMissing = !eventOrders;

        if (tabNeedsOrders) {
            const pathChanged = location.pathname !== lastFetchedPath.current;
            const shouldRefetchForTab = TABS_REFETCHED_ON_ENTRY.test(location.pathname);

            if (ticketsMissing || ordersMissing || (pathChanged && shouldRefetchForTab)) {
                lastFetchedPath.current = location.pathname;
                // Arriving from sell-stats, only the order lists are missing.
                getAllUserEventRelevantData({ includeOrders: true, includeExtras: ticketsMissing || (pathChanged && shouldRefetchForTab) });
            }
            return;
        }

        // sell-stats / links read neither list, so they never trigger an orders fetch.
        // The orders tab fetches them when the user actually opens it.
        if (ticketsMissing) {
            getAllUserEventRelevantData({ includeOrders: false });
        }
    }, [location.pathname, event?._id, eventTickets?.length, eventOrders?.length]);

    const isAllRelevantEventDataFetched = event && eventTickets && (!tabNeedsOrders || eventOrders);

    if(isLoading || !isAllRelevantEventDataFetched) {
        return  <LinearLoader />
    }

    return <Outlet />;
};

